import { type FormBuilderData, useUpdateForm, useUpsertBuilderInput } from "@/features/forms/api/formBuilderApi";
import type { Database } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { Switch } from "@patient-watch/ui/switch";
import { useEffect, useState } from "react";

type InputRow = Database["public"]["Tables"]["inputs"]["Row"];

const NUMERIC_INPUT_KEYS = new Set(["number", "slider", "scale-rating", "pain-slider", "glucose-slider"]);

const CHART_TYPES = [
    { value: "line", label: "Line", description: "Each score over time." },
    { value: "stacked_area", label: "Stacked area", description: "Subscales that add up to the total." },
    { value: "bar", label: "Bar", description: "One-off answers, such as global impression of change." },
] as const;

/** Scores that can be plotted: calculated scores first, then numeric questions. */
function chartableInputs(form: FormBuilderData): { input: InputRow; source: string }[] {
    const hidden = (form.hiddenInputs ?? []).map((input) => ({ input, source: "Calculated" }));
    const questions = form.pages.flatMap((page) =>
        (page.inputs as InputRow[])
            .filter((input) => input.chart_order != null || NUMERIC_INPUT_KEYS.has(input.input_key ?? ""))
            .map((input) => ({ input, source: page.page_label ?? `Page ${page.page_index}` }))
    );
    return [...hidden, ...questions];
}

/** Form builder Chart step: how this form's results are charted for clinicians. */
export function FormChartSettings({ form, formId }: { form: FormBuilderData; formId: string }) {
    const updateForm = useUpdateForm(formId);
    const showChart = form.show_chart !== false;
    const rows = chartableInputs(form);

    return (
        <section className="bg-background border rounded-xl overflow-hidden" data-testid="form-chart-settings">
            <div className="flex justify-between items-start gap-4 px-5 py-4 border-b">
                <div className="min-w-0">
                    <h2 className="font-semibold text-sm">Chart</h2>
                    <p className="text-muted-foreground text-xs text-pretty">
                        How this form&apos;s results are charted for clinicians.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Label htmlFor="form-show-chart" className="text-xs">
                        Show chart
                    </Label>
                    <Switch
                        id="form-show-chart"
                        checked={showChart}
                        onCheckedChange={(checked) => updateForm.mutate({ show_chart: checked })}
                    />
                </div>
            </div>

            <div className={showChart ? "space-y-6 p-6" : "space-y-6 p-6 opacity-50 pointer-events-none"}>
                <div className="gap-4 grid sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs">Chart type</Label>
                        <Select
                            value={form.chart_type ?? "line"}
                            onValueChange={(chart_type) => updateForm.mutate({ chart_type })}
                        >
                            <SelectTrigger className="text-sm">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CHART_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                        {type.label} - {type.description}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <MeaningfulChangeField
                        value={form.meaningful_change_threshold ?? ""}
                        onCommit={(next) => updateForm.mutate({ meaningful_change_threshold: next.trim() || null })}
                    />
                </div>

                <div className="space-y-2">
                    <div>
                        <p className="font-medium text-sm">Scores</p>
                        <p className="text-muted-foreground text-xs text-pretty">
                            Turn on the scores to plot and set their order. Primary scores decide whether a patient is
                            improving; with several, they are averaged first.
                        </p>
                    </div>
                    {rows.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No scores yet. Add calculated scores on the Calculator step.
                        </p>
                    ) : (
                        <div className="border rounded-lg divide-y">
                            <div className="items-center gap-3 grid grid-cols-[minmax(0,1fr)_4rem_4.5rem_5rem_4rem] px-3 py-2 text-2xs text-muted-foreground">
                                <span>Score</span>
                                <span>On chart</span>
                                <span>Order</span>
                                <span>Max</span>
                                <span>Primary</span>
                            </div>
                            {rows.map(({ input, source }) => (
                                <ChartSeriesRow key={input.input_id} formId={formId} input={input} source={source} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function MeaningfulChangeField({ value, onCommit }: { value: string; onCommit: (next: string) => void }) {
    const [draft, setDraft] = useState(value);
    useEffect(() => setDraft(value), [value]);
    return (
        <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs">Meaningful change</Label>
            <ui.Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => draft !== value && onCommit(draft)}
                placeholder="e.g. 12 points"
                className="text-sm"
            />
        </div>
    );
}

function NumberCell({
    value,
    onCommit,
    ariaLabel,
    disabled,
}: {
    value: number | null;
    onCommit: (next: number | null) => void;
    ariaLabel: string;
    disabled?: boolean;
}) {
    const [draft, setDraft] = useState(value == null ? "" : String(value));
    useEffect(() => setDraft(value == null ? "" : String(value)), [value]);
    return (
        <ui.Input
            type="number"
            aria-label={ariaLabel}
            disabled={disabled}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
                const next = draft.trim() === "" ? null : Number(draft);
                if (next !== value && (next == null || Number.isFinite(next))) onCommit(next);
            }}
            className="px-2 h-8 text-xs"
        />
    );
}

function ChartSeriesRow({ formId, input, source }: { formId: string; input: InputRow; source: string }) {
    const upsertInput = useUpsertBuilderInput(formId);
    const save = (changes: Partial<InputRow>) => upsertInput.mutate({ ...input, ...changes });
    const onChart = input.chart_order != null;

    return (
        <div className="items-center gap-3 grid grid-cols-[minmax(0,1fr)_4rem_4.5rem_5rem_4rem] px-3 py-2">
            <div className="min-w-0">
                <p className="text-sm truncate">{input.input_label || "Score"}</p>
                <p className="text-2xs text-muted-foreground truncate">{source}</p>
            </div>
            <Switch
                checked={onChart}
                aria-label={`Show ${input.input_label ?? "score"} on the chart`}
                onCheckedChange={(checked) => save({ chart_order: checked ? (input.input_index ?? 1) : null })}
            />
            <NumberCell
                ariaLabel="Chart order"
                value={input.chart_order}
                disabled={!onChart}
                onCommit={(chart_order) => save({ chart_order })}
            />
            <NumberCell
                ariaLabel="Maximum score"
                value={input.max_score}
                onCommit={(max_score) => save({ max_score })}
            />
            <Switch
                checked={input.is_primary_score === true}
                aria-label={`Use ${input.input_label ?? "score"} as a primary score`}
                onCheckedChange={(is_primary_score) => save({ is_primary_score })}
            />
        </div>
    );
}
